import { useEffect, useState, useCallback } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, TextField, IconButton, Box, Typography,
    Table, TableHead, TableBody, TableRow, TableCell,
    Select, MenuItem, FormControl, Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { createAmendment } from '../../api/commissionAmendmentApi';
import { getAllAgents } from '../../api/userApi';
import handleAgentName from '../../utils/userUtils';
import { handlePriceWithRM } from '../../utils/priceUtils';

const COMM_TYPES = ['PSC', 'DOC', 'TOC', 'RefC'];

const poppins = "'Poppins', sans-serif";

// Currency for figures that may legitimately be NEGATIVE (a DOC/TOC line the Upline/Team
// discount pushed below zero). Rounds to 2 dp FIRST so a −0.000001 float residue can never
// print as "- RM 0.00"; the codebase convention for the sign is handlePriceWithRM's "- RM".
const rm = (v) => {
    const n = Math.round((Number(v) || 0) * 100) / 100;
    return handlePriceWithRM(n === 0 ? 0 : n);
};

export default function AmendCommissionDialog({ open, onClose, onSaved, salesId, commissionLines, finalPrice, originalPrice, discountByAstern, discountByAgent, discountByUpline, discountByTeam, coAgentName }) {
    const [lines, setLines] = useState([]);
    const [reason, setReason] = useState('');
    const [agents, setAgents] = useState([]);
    const [submitting, setSubmitting] = useState(false);

    const fetchAgents = useCallback(async () => {
        try {
            const res = await getAllAgents();
            setAgents(res.data);
        } catch (e) {
            console.error('Failed to fetch agents', e);
        }
    }, []);

    useEffect(() => {
        if (open) fetchAgents();
    }, [open, fetchAgents]);

    const commBase = (originalPrice || 0) - (discountByAstern || 0);
    const commBaseHalf = commBase / 2;
    const agentDisc = discountByAgent || 0;
    // Upline / Team discount (spec §2). The upline discount comes off DOC and the team
    // discount off TOC, split across the recipients by their SplitPercent (and halved per
    // side on a co-broke). This dialog does NOT know each recipient's SplitPercent, so the
    // per-line share is recovered from the line itself when the dialog opens — see
    // `_discShare` below. Both reduce the 13.5% POOL by the same amount, keeping the
    // company residual neutral (spec Example D).
    const uplineDisc = discountByUpline || 0;
    const teamDisc = discountByTeam || 0;
    const hasNewDisc = uplineDisc !== 0 || teamDisc !== 0;
    const isCoBroke = !!coAgentName;

    useEffect(() => {
        if (open && commissionLines) {
            // Keep the "Total Commission" row (editable — Super Admin / Accountant may amend the
            // total pool). Only "Webbey Commission" stays out: it is the AUTO residual (Total −
            // payouts) and is recomputed on the backend. (user 2026-07-29)
            setLines(commissionLines
                .filter(l => l.commPayType !== 'Webbey Commission')
                .map(l => {
                    // How much of the Upline/Team discount THIS recipient already bears. The
                    // backend wrote `commPay = base × rate% − share`, so the share is simply the
                    // gap between the gross at the stored rate and the stored amount. Recovering
                    // it here means a rate amendment keeps the funded discount instead of
                    // silently paying the gross back (spec §7 / Example H).
                    // Only attempted when the sale actually carries one of the new buckets, so
                    // every pre-existing sale recomputes byte-identically (test 11).
                    const isDocToc = l.commPayType === 'DOC' || l.commPayType === 'TOC'
                        || l.commPayType === 'DOC/2' || l.commPayType === 'TOC/2';
                    let discShare = 0;
                    if (hasNewDisc && isDocToc && commBase && l.rate) {
                        const base = String(l.commPayType).includes('/2') ? commBaseHalf : (isCoBroke ? commBaseHalf : commBase);
                        const gross = base * l.rate / 100;
                        const gap = Math.round((gross - (l.commPay || 0)) * 100) / 100;
                        // Sub-ringgit gaps are rounding noise, not a funded discount.
                        if (Math.abs(gap) >= 0.5) discShare = gap;
                    }
                    return {
                        paymentTo: l.paymentTo,
                        paymentToName: l.paymentToName,
                        commPayType: l.commPayType,
                        commPay: l.commPay,
                        rate: l.rate,
                        editMode: 'rate', // 'rate' = edit rate, auto-calc amount; 'amount' = edit amount, auto-calc rate
                        isTotal: l.commPayType === 'Total Commission',
                        _discShare: discShare,
                    };
                })
            );
            setReason('');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, commissionLines]);

    // Detect if a PSC line uses FinalPrice (in-house agent) based on original values
    const isInHousePsc = (origRate, origAmount) => {
        if (!origRate || !finalPrice) return false;
        const fpCalc = finalPrice * origRate / 100;
        return Math.abs(fpCalc - origAmount) < 1; // within RM 1 tolerance
    };

    const updateLine = (index, field, value) => {
        setLines(prev => {
            const updated = [...prev];
            const line = { ...updated[index] };
            // Store original rate/amount from when dialog opened (for detecting in-house)
            if (!line._origRate) { line._origRate = line.rate; line._origAmount = line.commPay; }

            if (field === 'editMode') {
                line.editMode = value;
                updated[index] = line;
                return updated;
            }

            if (field === 'rate') {
                line.editMode = 'rate';
                const rate = parseFloat(value) || 0;
                line.rate = rate;

                if (line.commPayType === 'Total Commission') {
                    // Total pool = (Original − Astern) × rate% − AgentDisc − UplineDisc − TeamDisc.
                    // NOT floored at 0: every bucket cuts the pool by the same amount it cuts the
                    // individual line, which is what keeps the company residual neutral. Flooring
                    // here would hand the company a windfall on a heavily-discounted sale.
                    line.commPay = parseFloat((commBase * rate / 100 - agentDisc - uplineDisc - teamDisc).toFixed(2));
                } else if (line.commPayType === 'PSC') {
                    // In-house agent: FinalPrice × rate%
                    if (isInHousePsc(line._origRate, line._origAmount)) {
                        line.commPay = parseFloat((finalPrice * rate / 100).toFixed(2));
                    } else if (isCoBroke) {
                        // Co-broke PSC: (CommBase/2) × rate% - (AgentDisc/2)
                        line.commPay = parseFloat(Math.max(commBaseHalf * rate / 100 - agentDisc / 2, 0).toFixed(2));
                    } else {
                        // Normal PSC: CommBase × rate% - AgentDisc
                        line.commPay = parseFloat(Math.max(commBase * rate / 100 - agentDisc, 0).toFixed(2));
                    }
                } else {
                    // DOC, TOC (and DOC/2, TOC/2) — less this recipient's share of the Upline /
                    // Team discount (0 for every sale without one). The result is ALLOWED to be
                    // negative: when the discount exceeds the override, the bearer pays. Spec
                    // §12 decision ③ — allow negative, silently, no warning and no guard.
                    const base = line.commPayType.includes('/2') ? commBaseHalf : (isCoBroke ? commBaseHalf : commBase);
                    line.commPay = parseFloat((base * rate / 100 - (line._discShare || 0)).toFixed(2));
                }
            } else if (field === 'commPay') {
                line.editMode = 'amount';
                const amount = parseFloat(value) || 0;
                line.commPay = amount;

                // Reverse-calculate rate from amount
                if (line.commPayType === 'Total Commission') {
                    // amount = commBase × rate/100 − agentDisc − uplineDisc − teamDisc
                    //   → rate = (amount + agentDisc + uplineDisc + teamDisc) × 100 / commBase
                    line.rate = commBase ? parseFloat(((amount + agentDisc + uplineDisc + teamDisc) * 100 / commBase).toFixed(4)) : 0;
                } else if (line.commPayType === 'PSC') {
                    if (isInHousePsc(line._origRate, line._origAmount)) {
                        // amount = finalPrice × rate / 100 → rate = amount × 100 / finalPrice
                        line.rate = finalPrice ? parseFloat((amount * 100 / finalPrice).toFixed(4)) : 0;
                    } else if (isCoBroke) {
                        // amount = commBaseHalf × rate / 100 - agentDisc/2 → rate = (amount + agentDisc/2) × 100 / commBaseHalf
                        line.rate = commBaseHalf ? parseFloat(((amount + agentDisc / 2) * 100 / commBaseHalf).toFixed(4)) : 0;
                    } else {
                        // amount = commBase × rate / 100 - agentDisc → rate = (amount + agentDisc) × 100 / commBase
                        line.rate = commBase ? parseFloat(((amount + agentDisc) * 100 / commBase).toFixed(4)) : 0;
                    }
                } else {
                    // amount = base × rate/100 − discShare → rate = (amount + discShare) × 100 / base
                    const base = line.commPayType.includes('/2') ? commBaseHalf : (isCoBroke ? commBaseHalf : commBase);
                    line.rate = base ? parseFloat(((amount + (line._discShare || 0)) * 100 / base).toFixed(4)) : 0;
                }
            } else if (field === 'paymentTo') {
                line.paymentTo = value;
                const agent = agents.find(a => a.userId === value);
                line.paymentToName = agent
                    ? handleAgentName(agent.firstName, agent.lastName, agent.alias) || ''
                    : '';
            } else {
                line[field] = value;
            }

            updated[index] = line;
            return updated;
        });
    };

    const addRow = () => {
        setLines(prev => [...prev, { paymentTo: '', paymentToName: '', commPayType: 'PSC', commPay: 0, rate: 0, editMode: 'rate' }]);
    };

    const removeRow = (index) => {
        setLines(prev => prev.filter((_, i) => i !== index));
    };

    // The "Total Commission" row is the POOL, not a payout. Payout total = every line except it;
    // Webbey residual = Total − payouts. Editing Total below the payouts drives Webbey negative.
    const totalLine = lines.find(l => l.isTotal);
    const totalCommAmt = totalLine ? (parseFloat(totalLine.commPay) || 0) : null;
    const payoutTotal = lines.filter(l => !l.isTotal).reduce((sum, l) => sum + (parseFloat(l.commPay) || 0), 0);
    const webbeyResidual = totalCommAmt != null ? totalCommAmt - payoutTotal : null;

    const handleSubmit = async () => {
        if (!reason.trim()) { alert('Reason is required'); return; }
        setSubmitting(true);
        try {
            const proposedData = JSON.stringify(lines);
            await createAmendment({ salesId, proposedData, reason });
            alert('Amendment submitted for approval');
            onClose();
            onSaved();
        } catch (e) {
            alert('Failed: ' + (typeof e.response?.data === 'string' ? e.response.data : e.message));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '14px' } }}>
            <DialogTitle sx={{ fontFamily: poppins, fontWeight: 700, fontSize: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', pr: 1 }}>
                <span>Amend Commission — Sale #{salesId}</span>
                <IconButton onClick={onClose} size="small"><CloseIcon fontSize="small" /></IconButton>
            </DialogTitle>
            <DialogContent dividers>
                {payoutTotal > (finalPrice || 0) && (
                    <Alert severity="warning" sx={{ mb: 2, fontFamily: poppins, fontSize: '0.8rem' }}>
                        Payout total ({payoutTotal.toFixed(2)}) exceeds final price ({finalPrice?.toFixed(2)})
                    </Alert>
                )}
                {webbeyResidual != null && webbeyResidual < 0 && (
                    <Alert severity="error" sx={{ mb: 2, fontFamily: poppins, fontSize: '0.8rem' }}>
                        Webbey Commission would be <strong>negative ({webbeyResidual.toFixed(2)})</strong> — Total Commission ({totalCommAmt.toFixed(2)}) is below the sum of payouts ({payoutTotal.toFixed(2)}). Raise the Total or lower the payouts before submitting.
                    </Alert>
                )}

                {/* Discount context — the four buckets that shaped these lines. Read-only here:
                    the amounts themselves are rewritten on the sale (Accountant / Super Admin,
                    any status), which is what triggers the backend recompute. Shown so an
                    accountant can see WHY a DOC/TOC line is small or negative. */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                    {[
                        // Astern's comes off the price before the pool exists. The other three
                        // come out of the pool and together are the NEXWAVE GROUP discount, so
                        // they are named as parts of it rather than as four peers.
                        // (user 2026-10-23: "nexwave group discount = each individual discount")
                        { label: 'Astern Discount', value: discountByAstern },
                        { label: 'Nexwave Group — Agent', value: discountByAgent },
                        { label: 'Nexwave Group — Upline', value: discountByUpline },
                        { label: 'Nexwave Group — Team', value: discountByTeam },
                        { label: 'Nexwave Group — Total', value: agentDisc + uplineDisc + teamDisc },
                    ].map(d => (
                        <Box key={d.label} sx={{ background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '8px', px: 1.25, py: 0.75, minWidth: 130 }}>
                            <Typography sx={{ fontSize: '0.62rem', fontWeight: 600, color: '#94A3B8', textTransform: 'uppercase', fontFamily: poppins }}>{d.label}</Typography>
                            <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#0F172A', fontFamily: poppins }}>
                                {d.value != null ? rm(d.value) : '—'}
                            </Typography>
                        </Box>
                    ))}
                </Box>

                <Box sx={{ overflowX: 'auto' }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontFamily: poppins, fontWeight: 600, fontSize: '0.7rem', color: '#64748B', textTransform: 'uppercase' }}>Type</TableCell>
                                <TableCell sx={{ fontFamily: poppins, fontWeight: 600, fontSize: '0.7rem', color: '#64748B', textTransform: 'uppercase' }}>Agent</TableCell>
                                <TableCell sx={{ fontFamily: poppins, fontWeight: 600, fontSize: '0.7rem', color: '#64748B', textTransform: 'uppercase' }}>Rate (%)</TableCell>
                                <TableCell sx={{ fontFamily: poppins, fontWeight: 600, fontSize: '0.7rem', color: '#64748B', textTransform: 'uppercase' }}>Amount (RM)</TableCell>
                                <TableCell sx={{ width: 40 }}></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {lines.map((line, i) => (
                                <TableRow key={i} sx={{ '&:hover': { backgroundColor: '#F8FAFC' } }}>
                                    <TableCell sx={{ py: 0.5 }}>
                                        {line.isTotal ? (
                                            <Typography sx={{ fontFamily: poppins, fontSize: '0.78rem', fontWeight: 700, color: '#6366F1', whiteSpace: 'nowrap' }}>Total Commission</Typography>
                                        ) : (
                                            <FormControl size="small" sx={{ minWidth: 80 }}>
                                                <Select
                                                    value={line.commPayType}
                                                    onChange={e => updateLine(i, 'commPayType', e.target.value)}
                                                    sx={{ fontFamily: poppins, fontSize: '0.8rem' }}
                                                >
                                                    {COMM_TYPES.map(t => <MenuItem key={t} value={t} sx={{ fontFamily: poppins, fontSize: '0.8rem' }}>{t}</MenuItem>)}
                                                </Select>
                                            </FormControl>
                                        )}
                                    </TableCell>
                                    <TableCell sx={{ py: 0.5 }}>
                                        {line.isTotal ? (
                                            <Typography sx={{ fontFamily: poppins, fontSize: '0.8rem', fontWeight: 600, color: '#334155' }}>Webbey</Typography>
                                        ) : (
                                            <FormControl size="small" sx={{ minWidth: 160 }}>
                                                <Select
                                                    value={line.paymentTo || ''}
                                                    onChange={e => updateLine(i, 'paymentTo', e.target.value)}
                                                    displayEmpty
                                                    sx={{ fontFamily: poppins, fontSize: '0.8rem' }}
                                                >
                                                    <MenuItem value="" sx={{ fontFamily: poppins, fontSize: '0.8rem' }}><em>Select Agent</em></MenuItem>
                                                    {agents.map(a => (
                                                        <MenuItem key={a.userId} value={a.userId} sx={{ fontFamily: poppins, fontSize: '0.8rem' }}>
                                                            {handleAgentName(a.firstName, a.lastName, a.alias) || a.userId}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        )}
                                    </TableCell>
                                    <TableCell sx={{ py: 0.5 }}>
                                        <TextField
                                            type="number"
                                            size="small"
                                            value={line.rate}
                                            onChange={e => updateLine(i, 'rate', e.target.value)}
                                            onFocus={() => { if (line.editMode !== 'rate') updateLine(i, 'editMode', 'rate'); }}
                                            inputProps={{ step: 0.01, min: 0, style: { fontFamily: poppins, fontSize: '0.8rem' } }}
                                            sx={{
                                                width: 90,
                                                '& .MuiOutlinedInput-root': {
                                                    backgroundColor: line.editMode === 'rate' ? '#EFF6FF' : '#F8FAFC',
                                                    border: line.editMode === 'rate' ? '1.5px solid #1A8CFF' : '1px solid #E2E8F0',
                                                    '& fieldset': { border: 'none' },
                                                },
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell sx={{ py: 0.5 }}>
                                        <TextField
                                            type="number"
                                            size="small"
                                            value={line.commPay}
                                            onChange={e => updateLine(i, 'commPay', e.target.value)}
                                            onFocus={() => { if (line.editMode !== 'amount') updateLine(i, 'editMode', 'amount'); }}
                                            // NO `min: 0` — a DOC/TOC line whose Upline/Team discount
                                            // exceeds the override is legitimately NEGATIVE and must be
                                            // keyable here. Negatives are coloured, never blocked.
                                            inputProps={{ step: 0.01, style: { fontFamily: poppins, fontSize: '0.8rem', color: (parseFloat(line.commPay) || 0) < 0 ? '#DC2626' : undefined, fontWeight: (parseFloat(line.commPay) || 0) < 0 ? 700 : undefined } }}
                                            sx={{
                                                width: 110,
                                                '& .MuiOutlinedInput-root': {
                                                    backgroundColor: line.editMode === 'amount' ? '#EFF6FF' : '#F8FAFC',
                                                    border: line.editMode === 'amount' ? '1.5px solid #1A8CFF' : '1px solid #E2E8F0',
                                                    '& fieldset': { border: 'none' },
                                                },
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell sx={{ py: 0.5 }}>
                                        {!line.isTotal && (
                                            <IconButton size="small" onClick={() => removeRow(i)} sx={{ color: '#EF4444' }}>
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1.5, mb: 2 }}>
                    <Button
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={addRow}
                        sx={{ textTransform: 'none', fontFamily: poppins, fontSize: '0.8rem', color: '#1A8CFF' }}
                    >
                        Add Row
                    </Button>
                    <Box sx={{ textAlign: 'right' }}>
                        {/* Payout total NETS negative lines rather than dropping or flooring
                            them — a −RM151.47 DOC genuinely reduces what is paid out. */}
                        <Typography sx={{ fontFamily: poppins, fontSize: '0.85rem', fontWeight: 600, color: payoutTotal < 0 ? '#DC2626' : '#0F172A' }}>
                            Payouts: {rm(payoutTotal)}
                        </Typography>
                        {webbeyResidual != null && (
                            <Typography sx={{ fontFamily: poppins, fontSize: '0.75rem', fontWeight: 600, color: webbeyResidual < 0 ? '#DC2626' : '#64748B' }}>
                                Webbey (residual): {rm(webbeyResidual)}
                            </Typography>
                        )}
                    </Box>
                </Box>

                <TextField
                    fullWidth
                    multiline
                    rows={3}
                    size="small"
                    label="Reason for Amendment"
                    value={reason}
                    onChange={e => setReason(e.target.value)}
                    placeholder="Explain why the commission needs to be amended..."
                    required
                    sx={{ mt: 1 }}
                    InputProps={{ sx: { fontFamily: poppins, fontSize: '0.85rem' } }}
                    InputLabelProps={{ sx: { fontFamily: poppins, fontSize: '0.85rem' } }}
                />
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
                <Button onClick={onClose} sx={{ fontFamily: poppins, textTransform: 'none' }}>Cancel</Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={submitting}
                    sx={{
                        fontFamily: poppins, fontWeight: 600, textTransform: 'none', borderRadius: '8px',
                        background: 'linear-gradient(135deg, #1A8CFF, #0F62CC)',
                    }}
                >
                    {submitting ? 'Submitting...' : 'Submit for Approval'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
